<script setup lang="ts">
import type { MarkingSection } from '#shared/utils/markings/schema'
import type { LawAndCommentary, LawCommentaryNodeType } from '~/utils/solr/law-and-commentary'
import { LawFootnote, LawLink } from '#components'
import { markingNodeType } from '~/utils/markings'
import { faArrowRight, faBooks, faPaperclip } from '@fortawesome/pro-light-svg-icons'
import type { LawCitationInfo } from '~/utils/solr/law-utils'
import { handleHighlights } from '~/utils/solr/highlighting-utils'
import { hasLeadingBodytextLedd, shouldShowOwnBodytext } from '~/utils/solr/law-utils'

const props = withDefaults(defineProps<{
  node: LawAndCommentary
  selectedNodeId?: string | null
  searchResult: { id: string, highlights: string[] } | null
  searchResults: Array<{ id: string, highlights: string[] }> | null
  comments: Set<string>
  law?: LawCitationInfo
  hasCuratedReferences?: boolean
  commentsWithCuratedReferences: Set<string>
  commentsWithRelatedLiterature: Set<string>
  isScrollTop?: boolean
  searchMode?: boolean
  level?: number
  markingAncestors?: MarkingSection[]
}>(), {
  level: 0,
  markingAncestors: () => [],
})
const { loggedIn } = useUserSession()

const asideContext = defineModel<AsideContext>('aside-context', { default: 'commentary' })

const params = useLawRouteParams()
const textRef = useTemplateRef<HTMLElement>('text')

const nodeId = computed(() => props.node.id.toLocaleLowerCase())
const markingSectionPath = computed<MarkingSection[]>(() => {
  const title = props.node.title?.trim()
  if (props.node.nodeType === 'law' || !title) {
    return props.markingAncestors
  }
  return [...props.markingAncestors, {
    kind: 'unit',
    nodeType: markingNodeType(props.node.nodeType),
    title,
    unitId: props.node.id,
    anchor: nodeId.value,
  }]
})

const highlightScrollId = computed(() => `highlight-${nodeId.value}`)

const { lovId, query } = useLawIdParams(nodeId.value)

const { open } = useLawView()

const matchingResult = computed(() => {
  if (!props.searchResults) {
    return null
  }

  return props.searchResults.find(result => result.id === props.node.id) || null
})

const isScrollTarget = computed(() => {
  if (!props.searchResult) {
    return false
  }

  return props.node.id === props.searchResult.id
})

const mainNodeContent = computed(() => {
  if (!matchingResult.value) {
    return props.node.bodytext || ''
  }
  return handleHighlights(
    props.node.bodytext,
    matchingResult.value.highlights,
    isScrollTarget.value ? highlightScrollId.value : '',
  )
})

const isStandardText = useIsStandardText()

// NS standards nest deeper than laws — subsections can have their own children
// and need full section chrome (incl. commentary button) instead of inline rendering.
const secondaryNodeTypes = computed<LawCommentaryNodeType[]>(() =>
  isStandardText.value ? ['note', 'footnote'] : ['subsection', 'note', 'footnote'],
)

const secondaryNodes = computed(() => {
  if (props.node.nodeType !== 'section') {
    return []
  }

  const urlNodeId = params.value.nodeId?.toLocaleLowerCase()
  return (props.node.children?.filter(child => child.nodeType && secondaryNodeTypes.value.includes(child.nodeType))
    .map((node) => {
      const childMatch = props.searchResults?.find(r => r.id === node.id)
      const childIsScrollTarget = props.searchResult?.id === node.id
      return {
        ...node,
        isSelected: node.id.toLocaleLowerCase() === urlNodeId,
        highlightedBodytext: childMatch
          ? handleHighlights(node.bodytext, childMatch.highlights, childIsScrollTarget ? highlightScrollId.value : '')
          : node.bodytext,
      } satisfies LawAndCommentary & { isSelected: boolean, highlightedBodytext: string }
    })
  ) || []
})

const otherNodes = computed(() => {
  return props.node.children?.filter(child => child.nodeType && !secondaryNodeTypes.value.includes(child.nodeType)) || []
})

const showBodytext = computed(() => shouldShowOwnBodytext(props.node, isStandardText.value))

const inlineSubsections = computed(() =>
  secondaryNodes.value.filter(s => s.nodeType === 'subsection'),
)

const inlineFootnotes = computed(() =>
  secondaryNodes.value.filter(s => s.nodeType === 'note' || s.nodeType === 'footnote'),
)

const isSelfSelected = computed(() => nodeId.value === props.selectedNodeId)

const isSelectedNode = computed(() => {
  const isSecondarySelected = secondaryNodes.value?.some(secondary => secondary.isSelected)

  return isSelfSelected.value || isSecondarySelected
})

// The first ledd is stored on the section body text, so there is no subsection node to bold. Bold it when the section itself is selected
const isFirstLeddSelected = computed(() =>
  isSelfSelected.value && hasLeadingBodytextLedd(props.node, isStandardText.value),
)

function handleCuratedButtonClick(event: MouseEvent) {
  asideContext.value = 'curated'
  open('curated', event.currentTarget as HTMLElement)
  if (!isSelectedNode.value) {
    navigateTo({ name: 'law-by-id', params: { lovId: lovId.value }, query: query.value })
  }
}

function handleLiteratureButtonClick(event: MouseEvent) {
  asideContext.value = 'literature'
  open('literature', event.currentTarget as HTMLElement)
  if (!isSelectedNode.value) {
    navigateTo({ name: 'law-by-id', params: { lovId: lovId.value }, query: query.value })
  }
}

function handleCommentaryClick(event: MouseEvent) {
  asideContext.value = 'commentary'
  open('commentary', event.currentTarget as HTMLElement)
}

const { scrollToElement } = useScrollToElement('content')

const selectedSubsectionId = computed(() =>
  props.node.nodeType === 'section'
    ? secondaryNodes.value.find(subsection => subsection.isSelected)?.id.toLowerCase()
    : null,
)

const hasMarkingScrollTarget = useMarkingScrollTarget()

watch([textRef, isSelectedNode, selectedSubsectionId], ([el, isSelected, subId]) => {
  if (!el || !isSelected || hasMarkingScrollTarget.value) {
    return
  }

  if (props.searchResult?.id && props.node.id === props.searchResult.id) {
    return
  }

  if (isSelected && !subId) {
    scrollToElement(el)
  }
  else if (props.node.nodeType === 'section' && subId) {
    scrollToElement(subId, true)
  }
}, {
  immediate: true,
})

useInternalLinkHandler(textRef, 'content')

const isLaw = computed(() => props.node.nodeType === 'law')

const showAsSelected = computed(() => {
  if (isLaw.value) {
    return isSelectedNode.value && props.comments.has(nodeId.value)
  }
  else {
    return isSelectedNode.value
  }
})

const showCuratedButton = computed(() => props.commentsWithCuratedReferences.has(nodeId.value))
const showRelatedLiteratureButton = computed(() => props.commentsWithRelatedLiterature.has(nodeId.value))

const nsNestedVariant = computed<'deep' | undefined>(() => {
  if (!isStandardText.value || props.level === 0) {
    return undefined
  }
  // Every NS node reuses the law's default chrome — only subsections lose the
  // border so the underpunkter under a punkt read as one continuous block.
  return props.node.nodeType === 'subsection' ? 'deep' : undefined
})

watch(() => props.searchResult, (searchResult) => {
  if (searchResult?.id && props.node.id === searchResult.id) {
    nextTick(() => {
      scrollToElement(highlightScrollId.value, true)
    })
  }
})
</script>

<template>
  <div
    :id="nodeId"
    ref="text"
    :class="[lawVariants.text.base({ selected: showAsSelected, isTop: isScrollTop, law: isLaw, nsNested: nsNestedVariant }), nsNestedVariant === 'deep' ? 'ns-no-border' : isStandardText ? 'ns-bordered' : '']"
    data-citation-source="law"
    data-marking-content-type="law"
    :data-marking-work-id="law?.id"
    :data-marking-document-id="law?.id"
    :data-marking-unit-id="node.id"
    :data-marking-unit-title="node.title"
    :data-marking-node-type="markingNodeType(node.nodeType)"
    :data-marking-section-path="JSON.stringify(markingSectionPath)"
    :data-citation-law-id="law?.id"
    :data-citation-title="law?.title"
    :data-citation-short-title="law?.shortTitle"
    :data-citation-paragraph-title="node.title"
    :data-citation-timestamp="node.timestamp ?? undefined"
    :data-citation-law-timestamp="law?.timestamp ?? undefined"
    :data-citation-date-of-publication="law?.dateOfPublication ?? undefined"
    :data-citation-date-in-force="law?.dateInForce ?? undefined"
    :data-citation-last-change-in-force="law?.lastChangeInForce ?? undefined"
    :data-solr-id="node.id"
    :data-solr-timestamp="node.timestamp"
  >
    <div class="flex flex-col-reverse md:flex-row justify-between gap-xs px-s lg:px-m py-xs">
      <LawHeader v-if="isLaw" :node="node" :is-selected="isSelectedNode && comments.has(nodeId)" :is-scroll-top="isScrollTop" />
      <LawTitle v-else :id="node.id" :node-type="node.nodeType" :title="node.title" :title-html="node.titleHtml" />
      <div class="flex items-center self-start gap-2.5xs" data-marking-exclude>
        <Tooltip v-if="showRelatedLiteratureButton" label="Relevant litteratur">
          <template #trigger>
            <ControlButton
              :icon="faBooks"
              :selected="isSelectedNode && asideContext === 'literature'"
              aria-label="Se relatert litteratur"
              :class="{
                'bg-surface-invert dark:bg-carbon-50 text-carbon-50 dark:text-carbon-500': isSelectedNode && asideContext === 'literature',
                'text-primary bg-surface-primary dark:bg-surface-primary hover:bg-surface-secondary': !isSelectedNode,
                'text-primary bg-surface-primary dark:bg-surface-secondary hover:bg-surface-secondary': (isSelectedNode && asideContext !== 'literature'),
              }"
              @click.prevent="handleLiteratureButtonClick($event)"
            />
          </template>
        </Tooltip>
        <Tooltip v-if="showCuratedButton" label="Utvalgte kilder">
          <template #trigger>
            <ControlButton
              :icon="faPaperclip"
              :selected="isSelectedNode && asideContext === 'curated'"
              aria-label="Se utvalgte kilder"
              :class="{
                'bg-surface-invert dark:bg-carbon-50 text-carbon-50 dark:text-carbon-500': isSelectedNode && asideContext === 'curated',
                'text-primary bg-surface-primary dark:bg-surface-primary hover:bg-surface-secondary': !isSelectedNode,
                'text-primary bg-surface-primary dark:bg-surface-secondary hover:bg-surface-secondary': (isSelectedNode && asideContext !== 'curated'),
              }"
              @click.prevent="handleCuratedButtonClick($event)"
            />
          </template>
        </Tooltip>
        <LawLink
          v-if="comments.has(nodeId) && loggedIn"
          :law="nodeId"
          :is-active-search-result="isSelectedNode && searchMode"
          class="flex items-center no-underline gap-3xs p-2xs rounded-lg cursor-pointer select-none"
          :class="{
            'bg-surface-invert dark:bg-carbon-50 text-carbon-50 dark:text-carbon-500': isSelectedNode && asideContext === 'commentary',
            'text-primary bg-surface-primary dark:bg-surface-primary hover:bg-surface-secondary': !isSelectedNode,
            'text-primary bg-surface-primary dark:bg-surface-secondary hover:bg-surface-secondary': (isSelectedNode && asideContext !== 'commentary'),
          }"
          aria-label="Les lovkommentar"
          @click="handleCommentaryClick($event)"
        >
          <span class="text-caption">{{ node.nodeType === 'law' ? 'Innledende deler' : 'Kommentar' }}</span>
          <Icon :class="isSelectedNode && asideContext === 'commentary' ? 'text-white dark:text-carbon-500' : 'text-primary'" :icon="faArrowRight" size="sm" />
        </LawLink>
      </div>
    </div>

    <template v-if="showBodytext">
      <!-- Match the selected subsection style so the first ledd is bolded too -->
      <div class="px-s lg:px-m" :class="[isStandardText ? 'ns-bodytext' : 'law-bodytext', { 'font-semibold': isFirstLeddSelected }]" v-html="mainNodeContent" />
    </template>

    <div v-if="node.nodeType === 'section' && inlineSubsections.length" :class="lawVariants.section.base({ type: 'subsections' })">
      <LawNode
        v-for="secondaryNode in inlineSubsections"
        :key="secondaryNode.id"
        :node="secondaryNode"
        :law="law"
      >
        <div v-html="secondaryNode.highlightedBodytext" />
      </LawNode>
    </div>
    <div
      v-if="node.nodeType === 'section' && inlineFootnotes.length"
      id="footnotes-container"
      data-marking-exclude
      :class="lawVariants.section.base({ type: 'footnotes' })"
    >
      <LawNode
        v-for="secondaryNode in inlineFootnotes"
        :key="secondaryNode.id"
        :node="secondaryNode"
        :law="law"
      >
        <div v-if="secondaryNode.nodeType === 'note'" :class="lawVariants.note.base()">
          <span class="footnoteLabel"><span class="sr-only">Fotnote </span>0</span>
          <div v-html="secondaryNode.highlightedBodytext" />
        </div>
        <LawFootnote v-if="secondaryNode.nodeType === 'footnote'" :html="secondaryNode.highlightedBodytext" />
      </LawNode>
    </div>
  </div>
  <LawText
    v-for="child in otherNodes"
    :key="child.id"
    v-model:aside-context="asideContext"
    :node="child"
    :comments="comments"
    :search-result="searchResult"
    :search-results="searchResults"
    :law="law"
    :selected-node-id="selectedNodeId"
    :comments-with-curated-references="commentsWithCuratedReferences"
    :comments-with-related-literature="commentsWithRelatedLiterature"
    :search-mode="searchMode"
    :level="level + 1"
    :marking-ancestors="markingSectionPath"
  />
</template>

<style scoped lang="postcss">
:deep(.highlight) {
  @apply bg-blurple-100;
}

/* NS standards' bodytext uses `<br>` where authors mean paragraph breaks.
 * Chrome's UA layout treats `<br>` as a non-stylable line-break primitive, so
 * the Solr normalization step swaps each one for `<span class="ns-br">` — a
 * styleable element that respects `display: block` cross-browser. */
.ns-bodytext :deep(.ns-br) {
  display: block;
  margin-top: 0.75em;
}

.ns-bodytext :deep(ul),
.ns-bodytext :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.75em;
}

/* When a bordered NS node immediately follows a borderless one (a subsection),
 * the subsection has no bottom divider — so we mirror the law's border-bottom
 * onto the next bordered node's top edge to keep the visual separation. */
.ns-no-border + .ns-bordered {
  @apply border-t-4 border-surface-primary;
}

/* Markers are rendered in .data-originalId, use a hanging gutter instead of native list markers. */
.law-bodytext :deep(ol),
.law-bodytext :deep(ul) {
  @apply list-none my-3xs dark:text-white;
}

.law-bodytext :deep(li) {
  @apply pl-[1.75rem] mt-2xs dark:text-white;
}

.law-bodytext :deep(li > .data-originalId:first-child) {
  @apply inline-block min-w-[1.75rem] -ml-[1.75rem];
}
</style>
